import React, { useState, useEffect, useCallback } from 'react';
import '../styles/Habitos.css';
import { API_URL } from '../config';

// ─── Tipos ────────────────────────────────────────────────
interface HabitoDiario {
    id: number;
    fecha: string;
    sin_gluten: boolean;
    sin_azucar: boolean;
    deficit_calorico: boolean;
    sin_alcohol: boolean;
    triple_magnesio: boolean;
    omega_3: boolean;
    eutirox: boolean;
    creatina: boolean;
    proteina: boolean;
}

type Tab = 'hoy' | 'semana' | 'mes';
type CampoHabito = keyof Omit<HabitoDiario, 'id' | 'fecha'>;

// ─── Definición de hábitos ────────────────────────────────
const RESTRICCIONES: { key: CampoHabito; label: string; emoji: string }[] = [
    { key: 'sin_gluten',       label: 'No comí gluten',        emoji: '🌾' },
    { key: 'sin_azucar',       label: 'No comí azúcar',        emoji: '🍬' },
    { key: 'deficit_calorico', label: 'Hice déficit calórico', emoji: '🔥' },
    { key: 'sin_alcohol',      label: 'No tomé alcohol',       emoji: '🍷' },
];

const SUPLEMENTOS: { key: CampoHabito; label: string; emoji: string }[] = [
    { key: 'triple_magnesio', label: 'Tomé triple magnesio', emoji: '💊' },
    { key: 'omega_3',         label: 'Tomé omega 3',         emoji: '🐟' },
    { key: 'eutirox',         label: 'Tomé eutirox',         emoji: '💊' },
    { key: 'creatina',        label: 'Tomé creatina',        emoji: '💪' },
    { key: 'proteina',        label: 'Tomé proteína',        emoji: '🥤' },
];

const TODOS = [...RESTRICCIONES, ...SUPLEMENTOS];

// ─── Helpers de fecha ─────────────────────────────────────
function toISO(d: Date) {
    return d.toISOString().slice(0, 10);
}

function addDays(d: Date, n: number) {
    const r = new Date(d);
    r.setDate(r.getDate() + n);
    return r;
}

function formatFecha(iso: string) {
    const [y, m, d] = iso.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    return date.toLocaleDateString('es-CL', { weekday: 'long', day: 'numeric', month: 'long' });
}

const DIAS_CORTO = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

// ─── Componente ───────────────────────────────────────────
const Habitos: React.FC = () => {
    const [tab, setTab] = useState<Tab>('hoy');
    const [fechaSeleccionada, setFechaSeleccionada] = useState(toISO(new Date()));
    const [registro, setRegistro] = useState<HabitoDiario | null>(null);
    const [loadingHoy, setLoadingHoy] = useState(false);
    const [toggling, setToggling] = useState<CampoHabito | null>(null);

    const [semana, setSemana] = useState<HabitoDiario[]>([]);
    const [loadingSemana, setLoadingSemana] = useState(false);

    const [mes, setMes] = useState<HabitoDiario[]>([]);
    const [loadingMes, setLoadingMes] = useState(false);

    const hoy = toISO(new Date());

    // ── Cargar registro del día seleccionado ──────────────
    const cargarHoy = useCallback(async (fecha: string) => {
        setLoadingHoy(true);
        try {
            const res = await fetch(`${API_URL}/api/habitos/hoy/?fecha=${fecha}`);
            if (res.ok) setRegistro(await res.json());
        } catch { /* silencioso */ }
        finally { setLoadingHoy(false); }
    }, []);

    useEffect(() => { if (tab === 'hoy') cargarHoy(fechaSeleccionada); }, [fechaSeleccionada, tab, cargarHoy]);

    // ── Cargar semana ─────────────────────────────────────
    const cargarSemana = useCallback(async () => {
        setLoadingSemana(true);
        try {
            const res = await fetch(`${API_URL}/api/habitos/semana/`);
            if (res.ok) setSemana(await res.json());
        } catch { /* silencioso */ }
        finally { setLoadingSemana(false); }
    }, []);

    useEffect(() => { if (tab === 'semana') cargarSemana(); }, [tab, cargarSemana]);

    // ── Cargar mes ────────────────────────────────────────
    const cargarMes = useCallback(async () => {
        setLoadingMes(true);
        try {
            const res = await fetch(`${API_URL}/api/habitos/mes/`);
            if (res.ok) setMes(await res.json());
        } catch { /* silencioso */ }
        finally { setLoadingMes(false); }
    }, []);

    useEffect(() => { if (tab === 'mes') cargarMes(); }, [tab, cargarMes]);

    // ── Toggle hábito ─────────────────────────────────────
    const toggle = async (campo: CampoHabito) => {
        if (!registro || toggling) return;
        setToggling(campo);
        // Optimistic update
        setRegistro(prev => prev ? { ...prev, [campo]: !prev[campo] } : prev);
        try {
            const res = await fetch(`${API_URL}/api/habitos/${registro.id}/toggle/`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ campo }),
            });
            if (res.ok) setRegistro(await res.json());
            else setRegistro(prev => prev ? { ...prev, [campo]: !prev[campo] } : prev); // revert
        } catch {
            setRegistro(prev => prev ? { ...prev, [campo]: !prev[campo] } : prev); // revert
        }
        setToggling(null);
    };

    // ── Progreso del día ──────────────────────────────────
    const totalCheck = registro ? TODOS.filter(h => registro[h.key]).length : 0;
    const totalHabitos = TODOS.length;
    const pct = totalHabitos > 0 ? Math.round((totalCheck / totalHabitos) * 100) : 0;

    // ── Semana: últimos 7 días ────────────────────────────
    const diasSemana: string[] = Array.from({ length: 7 }, (_, i) =>
        toISO(addDays(new Date(), i - 6))
    );
    const mapasSemana = Object.fromEntries(semana.map(r => [r.fecha, r]));

    // ── Mes: calcular % y racha por hábito ────────────────
    const calcularStats = (key: CampoHabito) => {
        if (mes.length === 0) return { pct: 0, racha: 0 };
        const diasConRegistro = mes.filter(r => r[key]);
        const pctMes = Math.round((diasConRegistro.length / mes.length) * 100);

        // Racha: días consecutivos desde hoy hacia atrás
        const fechasOrdenadas = [...mes].sort((a, b) => b.fecha.localeCompare(a.fecha));
        let racha = 0;
        let prev: Date | null = null;
        for (const r of fechasOrdenadas) {
            if (!r[key]) break;
            const curr = new Date(r.fecha + 'T12:00:00');
            if (prev === null) {
                const diffHoy = (new Date(hoy + 'T12:00:00').getTime() - curr.getTime()) / 86400000;
                if (diffHoy > 1) break;
            } else {
                const diff = (prev.getTime() - curr.getTime()) / 86400000;
                if (diff > 1) break;
            }
            racha++;
            prev = curr;
        }
        return { pct: pctMes, racha };
    };

    // ─────────────────────────────────────────────────────
    return (
        <div className="habitos-page">
            {/* Header */}
            <div className="habitos-header">
                <div className="habitos-header-inner">
                    <span className="habitos-header-icon">✅</span>
                    <div>
                        <h1 className="habitos-header-title">Hábitos Diarios</h1>
                        <p className="habitos-header-sub">Registro personal de Ronald</p>
                    </div>
                </div>
            </div>

            <div className="habitos-content">
                {/* Tabs */}
                <div className="habitos-tabs">
                    {(['hoy', 'semana', 'mes'] as Tab[]).map(t => (
                        <button
                            key={t}
                            className={`habitos-tab${tab === t ? ' active' : ''}`}
                            onClick={() => setTab(t)}
                        >
                            {t === 'hoy' ? 'Hoy' : t === 'semana' ? 'Esta semana' : 'Este mes'}
                        </button>
                    ))}
                </div>

                {/* ── TAB HOY ─────────────────────────────────── */}
                {tab === 'hoy' && (
                    <>
                        {/* Navegación de fecha */}
                        <div className="habitos-fecha-row">
                            <button
                                className="habitos-fecha-btn"
                                onClick={() => setFechaSeleccionada(toISO(addDays(new Date(fechaSeleccionada + 'T12:00:00'), -1)))}
                            >‹</button>
                            <span className="habitos-fecha-label">{formatFecha(fechaSeleccionada)}</span>
                            <button
                                className="habitos-fecha-btn"
                                disabled={fechaSeleccionada >= hoy}
                                onClick={() => setFechaSeleccionada(toISO(addDays(new Date(fechaSeleccionada + 'T12:00:00'), 1)))}
                            >›</button>
                        </div>

                        {loadingHoy ? (
                            <div className="habitos-loading">Cargando...</div>
                        ) : registro ? (
                            <>
                                {/* Barra de progreso */}
                                <div className="habitos-progress-wrap">
                                    <span className="habitos-progress-text">{totalCheck}/{totalHabitos} hechos</span>
                                    <div className="habitos-progress-bar-bg">
                                        <div className="habitos-progress-bar-fill" style={{ width: `${pct}%` }} />
                                    </div>
                                    <span className="habitos-progress-pct">{pct}%</span>
                                </div>

                                {/* Restricciones */}
                                <div className="habitos-card">
                                    <div className="habitos-card-title">Restricciones</div>
                                    {RESTRICCIONES.map(h => (
                                        <div
                                            key={h.key}
                                            className="habitos-item"
                                            onClick={() => toggle(h.key)}
                                        >
                                            <div className={`habitos-item-check${registro[h.key] ? ' checked' : ''}`}>
                                                <svg viewBox="0 0 12 12">
                                                    <polyline points="1.5,6 5,9.5 10.5,2.5" />
                                                </svg>
                                            </div>
                                            <span className="habitos-item-emoji">{h.emoji}</span>
                                            <span className={`habitos-item-label${registro[h.key] ? ' done' : ''}`}>
                                                {h.label}
                                            </span>
                                        </div>
                                    ))}
                                </div>

                                {/* Suplementos */}
                                <div className="habitos-card">
                                    <div className="habitos-card-title">Suplementos</div>
                                    {SUPLEMENTOS.map(h => (
                                        <div
                                            key={h.key}
                                            className="habitos-item"
                                            onClick={() => toggle(h.key)}
                                        >
                                            <div className={`habitos-item-check${registro[h.key] ? ' checked' : ''}`}>
                                                <svg viewBox="0 0 12 12">
                                                    <polyline points="1.5,6 5,9.5 10.5,2.5" />
                                                </svg>
                                            </div>
                                            <span className="habitos-item-emoji">{h.emoji}</span>
                                            <span className={`habitos-item-label${registro[h.key] ? ' done' : ''}`}>
                                                {h.label}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <div className="habitos-empty">No se pudo cargar el registro.</div>
                        )}
                    </>
                )}

                {/* ── TAB SEMANA ──────────────────────────────── */}
                {tab === 'semana' && (
                    loadingSemana ? (
                        <div className="habitos-loading">Cargando...</div>
                    ) : (
                        <div className="semana-card">
                            {/* Cabecera con días */}
                            <div className="semana-header-row">
                                <div />
                                {diasSemana.map(d => {
                                    const dia = new Date(d + 'T12:00:00');
                                    return (
                                        <div key={d} className={`semana-header-day${d === hoy ? ' hoy' : ''}`}>
                                            {DIAS_CORTO[dia.getDay()]}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Fila por hábito */}
                            {TODOS.map(h => (
                                <div key={h.key} className="semana-row">
                                    <div className="semana-habit-label">
                                        <span>{h.emoji}</span>
                                        <span>{h.label.replace(/^(No comí|No tomé|Tomé|Hice) /, '')}</span>
                                    </div>
                                    {diasSemana.map(d => {
                                        const reg = mapasSemana[d];
                                        const esFuturo = d > hoy;
                                        if (esFuturo) return <div key={d} className="semana-dot futuro" />;
                                        if (!reg) return <div key={d} className="semana-dot no">–</div>;
                                        return (
                                            <div key={d} className={`semana-dot${reg[h.key] ? ' ok' : ' no'}`}>
                                                {reg[h.key] ? '✓' : '–'}
                                            </div>
                                        );
                                    })}
                                </div>
                            ))}
                        </div>
                    )
                )}

                {/* ── TAB MES ─────────────────────────────────── */}
                {tab === 'mes' && (
                    loadingMes ? (
                        <div className="habitos-loading">Cargando...</div>
                    ) : mes.length === 0 ? (
                        <div className="habitos-empty">Sin registros este mes aún.</div>
                    ) : (
                        <>
                            {/* Restricciones */}
                            <div className="mes-card">
                                <div className="habitos-card-title" style={{ padding: '16px 20px 12px', borderBottom: '1px solid #f1f5f9', fontSize: 11, fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase', color: '#94a3b8' }}>
                                    Restricciones
                                </div>
                                {RESTRICCIONES.map(h => {
                                    const { pct: p, racha } = calcularStats(h.key);
                                    return (
                                        <div key={h.key} className="mes-row">
                                            <div className="mes-row-top">
                                                <span className="mes-row-label">
                                                    <span>{h.emoji}</span>{h.label}
                                                </span>
                                                <div className="mes-row-stats">
                                                    {racha > 1 && <span className="mes-streak">🔥 {racha} días</span>}
                                                    <span className="mes-pct">{p}%</span>
                                                </div>
                                            </div>
                                            <div className="mes-bar-bg">
                                                <div className="mes-bar-fill" style={{ width: `${p}%` }} />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Suplementos */}
                            <div className="mes-card">
                                <div className="habitos-card-title" style={{ padding: '16px 20px 12px', borderBottom: '1px solid #f1f5f9', fontSize: 11, fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase', color: '#94a3b8' }}>
                                    Suplementos
                                </div>
                                {SUPLEMENTOS.map(h => {
                                    const { pct: p, racha } = calcularStats(h.key);
                                    return (
                                        <div key={h.key} className="mes-row">
                                            <div className="mes-row-top">
                                                <span className="mes-row-label">
                                                    <span>{h.emoji}</span>{h.label}
                                                </span>
                                                <div className="mes-row-stats">
                                                    {racha > 1 && <span className="mes-streak">🔥 {racha} días</span>}
                                                    <span className="mes-pct">{p}%</span>
                                                </div>
                                            </div>
                                            <div className="mes-bar-bg">
                                                <div className="mes-bar-fill" style={{ width: `${p}%` }} />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )
                )}
            </div>
        </div>
    );
};

export default Habitos;
